import React from 'react'

import { Header, Icon, Image } from 'semantic-ui-react'

import { IProject } from '../../../api/Data'
import moment from '../../../utils/Moment'
import { convertTextAreaToHTML } from '../../../utils/sharedFunctions'

import './ClassicUploaderPreview.scss'

type PreviewMode = 'mobile' | 'desktop'

const previewTranslations: Record<string, Record<string, string>> = {
  'en-gb': {
    submit: 'Submit content to',
    upload: 'Upload',
    record: 'Record HD video',
    recordingHelp: 'Uses your camera and microphone',
    qr: 'QR code',
    instructions: 'Instructions',
    guidelines: 'Guidelines',
    prize: 'Prize',
    rules: 'Project Rules',
    terms: 'Terms and conditions',
    requirements: 'File requirements',
    question: 'Question 1 of {{total}}',
    closed: 'Project closed',
  },
  fr: {
    submit: 'Envoyer',
    upload: 'Importer',
    record: 'Enregistrer une vidéo HD',
    recordingHelp: 'Utilise votre caméra et votre microphone',
    qr: 'Code QR',
    instructions: 'Instructions',
    guidelines: 'Conseils',
    prize: 'Prix',
    rules: 'Règles du projet',
    terms: 'Conditions générales',
    requirements: 'Exigences des fichiers',
    question: 'Question 1 sur {{total}}',
    closed: 'Projet fermé',
  },
  es: {
    submit: 'Enviar',
    upload: 'Subir',
    record: 'Grabar video HD',
    recordingHelp: 'Usa la cámara y el micrófono',
    qr: 'Código QR',
    instructions: 'Instrucciones',
    guidelines: 'Consejos',
    prize: 'Premio',
    rules: 'Reglas del proyecto',
    terms: 'Términos y condiciones',
    requirements: 'Requisitos de archivo',
    question: 'Pregunta 1 de {{total}}',
    closed: 'Proyecto cerrado',
  },
  de: {
    submit: 'Laden Sie Ihre Videos hoch zu',
    upload: 'Hochladen',
    record: 'HD-Video aufnehmen',
    recordingHelp: 'Verwendet Ihre Kamera und Ihr Mikrofon',
    qr: 'QR-Code',
    instructions: 'So nehmen Sie teil',
    guidelines: 'Tipps zum Filmen',
    prize: 'Belohnung',
    rules: 'Projektregeln',
    terms: 'Allgemeine Geschäftsbedingungen',
    requirements: 'Dateianforderungen',
    question: 'Frage 1 von {{total}}',
    closed: 'Projekt geschlossen',
  },
  it: {
    submit: 'Carica i tuoi video su',
    upload: 'Carica',
    record: 'Registra video HD',
    recordingHelp: 'Usa la fotocamera e il microfono',
    qr: 'Codice QR',
    instructions: 'Come partecipare',
    guidelines: 'Consigli di ripresa',
    prize: 'Premio',
    rules: 'Regole del progetto',
    terms: 'Termini e condizioni',
    requirements: 'Requisiti dei file',
    question: 'Domanda 1 di {{total}}',
    closed: 'Progetto chiuso',
  },
  nl: {
    submit: 'Content verzenden naar',
    upload: 'Uploaden',
    record: 'HD-video opnemen',
    recordingHelp: 'Gebruikt je camera en microfoon',
    qr: 'QR-code',
    instructions: 'Instructies',
    guidelines: 'Richtlijnen',
    prize: 'Prijs',
    rules: 'Projectregels',
    terms: 'Algemene voorwaarden',
    requirements: 'Bestandsvereisten',
    question: 'Vraag 1 van {{total}}',
    closed: 'Project gesloten',
  },
  'pt-br': {
    submit: 'Enviar',
    upload: 'Enviar',
    record: 'Gravar vídeo em HD',
    recordingHelp: 'Usa sua câmera e seu microfone',
    qr: 'Código QR',
    instructions: 'Instruções',
    guidelines: 'Dicas',
    prize: 'Prêmio',
    rules: 'Regras do projeto',
    terms: 'Termos e condições',
    requirements: 'Requisitos dos arquivos',
    question: 'Pergunta 1 de {{total}}',
    closed: 'Projeto encerrado',
  },
}

previewTranslations['en-us'] = previewTranslations['en-gb']

const SecondaryRow = ({ label }: { label: string }) => (
  <div className="classic-preview-disclosure">
    <strong>{label}</strong>
    <Icon name="chevron down" />
  </div>
)

const ClassicUploaderPreview = ({
  project,
  mode,
  teamName,
  logoAvatarUrl,
}: {
  project: IProject
  mode: PreviewMode
  teamName: string
  logoAvatarUrl: string
}) => {
  const {
    name,
    title,
    description,
    questions = [],
    numberOfQuestions = 1,
    incentive,
    rules,
    tips,
    teamTermsUrl,
    imageUrl,
    hasVideo,
    deadline,
    allowRecordVideo,
    allowUploadFile,
    allowQrCode,
  } = project

  const currentQuestion = questions[0]
  const labels = previewTranslations[project.locale] || previewTranslations['en-gb']
  const headline = name || title
  const showSubhead = !!name && !!title && name !== title
  const isProjectClosed = !!deadline && moment().isAfter(moment(deadline))
  const effectiveAllowUploadFile = project.projectType === 'video_conversation' ? false : allowUploadFile

  return (
    <div className={`classic-preview-frame is-${mode}`}>
      <div className="classic-preview-page">
        <section className="classic-preview-hero">
          <div className="classic-preview-hero-copy">
            <div className="classic-preview-brand">
              <Image src={logoAvatarUrl} alt={teamName} />
              <div>
                <span>{labels.submit}</span>
                <strong>{teamName}</strong>
              </div>
            </div>

            <Header as="h2">
              <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(headline || '') }} />
            </Header>
            {showSubhead && (
              <div
                className="classic-preview-subhead"
                dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(title || '') }}
              />
            )}
          </div>

          <div
            className="classic-preview-hero-media"
            style={{
              backgroundImage: `url(${imageUrl})`,
            }}
          >
            {hasVideo && <Icon name="play circle outline" />}
          </div>
        </section>

        {isProjectClosed ? (
          <div className="classic-preview-closed">{labels.closed}</div>
        ) : (
          <div className="classic-preview-columns">
            <section className="classic-preview-workspace">
              {!!currentQuestion && (
                <div className="classic-preview-prompt">
                  {numberOfQuestions > 1 && (
                    <span>{labels.question.replace('{{total}}', String(numberOfQuestions))}</span>
                  )}
                  <strong>{currentQuestion}</strong>
                </div>
              )}

              <div className="classic-preview-actions">
                {effectiveAllowUploadFile && (
                  <div className="classic-preview-action">
                    <Icon name="cloud upload" />
                    <strong>{labels.upload}</strong>
                  </div>
                )}

                {allowRecordVideo && (
                  <div className="classic-preview-action is-primary">
                    <Icon name="video camera" />
                    <strong>{labels.record}</strong>
                    <span>{labels.recordingHelp}</span>
                  </div>
                )}

                {mode === 'desktop' && allowQrCode && (
                  <div className="classic-preview-action">
                    <Icon name="qrcode" />
                    <strong>{labels.qr}</strong>
                  </div>
                )}
              </div>
            </section>

            <aside className="classic-preview-guidance">
              {!!description && (
                <section className="classic-preview-instructions">
                  <strong>{labels.instructions}</strong>
                  <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(description) }} />
                </section>
              )}
              {!!tips && <SecondaryRow label={labels.guidelines} />}
              {!!incentive && <SecondaryRow label={labels.prize} />}
              {!!rules && <SecondaryRow label={labels.rules} />}
              {!!teamTermsUrl && <SecondaryRow label={labels.terms} />}
              {effectiveAllowUploadFile && <SecondaryRow label={labels.requirements} />}
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}

export type { PreviewMode }
export default ClassicUploaderPreview
